import { Injectable } from '@angular/core';
import {
	HttpEvent,
	HttpInterceptor,
	HttpHandler,
	HttpRequest,
	HttpErrorResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, retryWhen, tap } from 'rxjs/operators';
import { JnjErrorHandler } from '../utilities/jnj-error-handler/jnj-error-handler.js';

@Injectable({ providedIn: 'root' })
export class HttpErrorInterceptor implements HttpInterceptor {
	constructor(private errorHandler: JnjErrorHandler) {}

	/** In general, all errors are handled and captured here. However, there
	 * may be cases when we need a more fine-grained approach to error handling.
	 * The magic link, for example, returnes a 429 with information for the user
	 * if too many requests are made. In these cases, the service will provide
	 * a handleError method to the service base and handle any non-500 errors there
	 *
	 * FUTURUE - if the servicebase could provide HttpParams or HttpContext with the request
	 * we could have it set programatically in the service, rather than maintained here
	 */
	private endpointsToRethrow = ['/user/magicLink'];

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
			retryWhen((errors) =>
				errors.pipe(
					tap((error) => {
						if (
							error?.message.indexOf(`0 Unknown`) > -1 &&
							error.url.indexOf('/api/v1/') > -1
						) {
							console.log(`ADJQ-1537: ${JSON.stringify(error)}`);
						} else {
							throw error;
						}
					})
				)
			),
			catchError((error: HttpErrorResponse) => {
				// We don't want to raise a toast for non-API HTTP errors
				// since, for example, we expect Juniverse to 401 for all
				// non-company users
				if (!this.isAPIError(error)) {
					return of(null);
				}

				if (error.status >= 500) {
					this.errorHandler.handleError(
						new Error(this.formatErrorMsg(error)),
						'HTTP Error',
						false,
						error.error?.correlationId
					);
				}

				if (
					this.endpointsToRethrow.some((endpoint) =>
						error.url.includes(endpoint)
					) &&
					error.status !== 500
				) {
					throw error;
				}

				return of(null);
			})
		);
	}

	public isAPIError(error: HttpErrorResponse): boolean {
		return (
			(error.status >= 500 || error.status === 429) &&
			error.url.indexOf('/api/v1/') > -1
		);
	}

	public formatErrorMsg(error: HttpErrorResponse): string {
		return error.error instanceof ErrorEvent
			? `Error: ${error.error.message}`
			: `Error Code: ${error.status},  Message: ${error.message}`;
	}
}
